import { TrendingUp, Anchor, Route, Ship } from "lucide-react";

const TONE = {
  positive: { bar: "#1baf7a", chip: "bg-emerald-50 text-emerald-700", icon: "text-emerald-600" },
  caution: { bar: "#eda100", chip: "bg-amber-50 text-amber-700", icon: "text-amber-600" },
  neutral: { bar: "#0047AB", chip: "bg-blue-50 text-primary", icon: "text-primary" },
};

const ICONS = [Anchor, Ship, TrendingUp, Route];

// Built-in fallback — used when no market-insight edition has been published.
export const DEFAULT_WATCH = {
  week_label: "Week 41 / 2026",
  source: "Alphaliner Newsletters 2026-38, 2026-39 & 2026-41 · Monthly Monitor Sep 2026 · Sea-Intelligence LP181",
  cards: [
    { tone: "caution", value: "0.7% idle", tag: "Fleet fully employed", desc: "Only 0.7% of the global cellular fleet is commercially idle (99 ships / 232,772 TEU, 5 Oct 2026) — down from 108 ships / 254,784 TEU on 21 Sep; neither Golden Week nor the Suez return has yet released idle capacity." },
    { tone: "neutral", value: "US$36,000/day", tag: "‘Bangkokmax’ charter", desc: "1,800 TEU ‘Bangkokmax’ feeders — the size that feeds Thailand and regional Australian ports — hold at US$36,000/day for 12 months (standard 1,700s at US$34,000), while 2,500 TEU ships rose to US$42,000/day (13 Oct 2026)." },
    { tone: "caution", value: "42.4% orderbook", tag: "▲ Newbuild wave 2027–28", desc: "The orderbook has reached 42.4% of the fleet (14.5M TEU / 1,772 ships); ~9.3M TEU delivers across 2027–28 — a structural overcapacity threat likely to cap any medium-term rate recovery, even though near-term supply stays tight." },
    { tone: "caution", value: "52.0%", tag: "Asia→Oceania reliability", desc: "Asia→Oceania schedule reliability fell to 52.0% in Jul/Aug 2026 (−8.0pp MoM, −13.4pp YoY) on severe Asian port congestion; the backhaul dropped to 43.2% and global reliability to 49.9% in Aug — lowest since Sep 2022 (Sea-Intelligence LP181, Sep 2026)." },
  ],
};

export function AlphalinerWatch({ data }) {
  const w = data && Array.isArray(data.cards) && data.cards.length ? data : DEFAULT_WATCH;
  return (
    <section className="border-b border-slate-200 bg-slate-50/70" data-testid="alphaliner-watch">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12">
        <div className="flex items-baseline gap-4 flex-wrap">
          <span className="font-num text-sm text-accent font-semibold whitespace-nowrap">
            {w.week_label}
          </span>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
            Carrier capacity moves that touch your lane
          </h2>
        </div>
        <p className="font-num text-[10px] uppercase tracking-wider text-slate-400 mt-2">
          Alphaliner Market Watch · Source · {w.source}
        </p>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mt-8">
          {w.cards.map((s, i) => {
            const t = TONE[s.tone] || TONE.neutral;
            const Icon = ICONS[i % ICONS.length];
            return (
              <div
                key={i}
                data-testid={`alphaliner-stat-${i}`}
                className="relative bg-white border border-slate-200 rounded-xl p-5 pl-6 overflow-hidden transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5"
              >
                <span className="absolute left-0 top-0 h-full w-1.5" style={{ background: t.bar }} />
                <div className="flex items-start justify-between gap-2">
                  <div className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium font-num ${t.chip}`}>
                    {s.tag}
                  </div>
                  <Icon className={`w-4 h-4 shrink-0 ${t.icon}`} />
                </div>
                <div className="font-display text-xl font-bold text-slate-900 mt-3 leading-tight">{s.value}</div>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{s.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
